import { Table, useTable, useTableInstance } from "ka-table";
import {
  ActionType,
  DataType,
  EditingMode,
  FilteringMode,
  PagingPosition,
  SortingMode,
} from "ka-table/enums";
import "./style.scss";
import { AttributeTableData, ChildComponents, Column } from "ka-table/models";
import { Button, FormGroup, Input } from "reactstrap";
import { loadData, updatePageSize } from "ka-table/actionCreators";
import PagingPages from "./pagingPages";
import { message } from "../toast";
import { ICellTextProps, IDataRowProps } from "ka-table/props";
import { useMemo, useState, useEffect } from "react";
import { close, searchIcon } from "../../constant/icons";
import { kaPropsUtils } from "ka-table/utils";

export declare class TableColumn extends Column {
  component?: any;
  valueConvert?: any;
  hideHeaderFilter?: Boolean;
  callBack?: (rowValue: any) => void;
  stopCallBack?: (rowValue: any) => void;
  destinationColumn?: string;
  destinationLName?: string;
  stopButton?: boolean;
}

export function generateUniqueId() {
  const timestamp = new Date().getTime();
  const random = Math.random() * 1000000;
  const uniqueId = `${timestamp}${random}`;
  return `Table${uniqueId}`;
}

const SelectionCell: React.FC<ICellTextProps> = ({
  rowKeyValue,
  isSelectedRow,
  selectedRows,
}) => {
  const table = useTableInstance();
  return (
    <div className="form-check" style={{ width: '1rem', margin: '0 auto' }}>
      <input
        className="form-check-input"
        type="checkbox"
        checked={isSelectedRow}
        value=""
        onChange={(event: any) => {
          if (event.nativeEvent.shiftKey) {
            table.selectRowsRange(rowKeyValue, [...selectedRows].pop());
          } else if (event.target.checked) {
            table.selectRow(rowKeyValue);
          } else {
            table.deselectRow(rowKeyValue);
          }
        }}
      />
    </div>
  );
};

const SelectionHeader = ({ allRows }: { allRows: any }) => {
  const table = useTableInstance();
  const areAllRowsSelected = kaPropsUtils.areAllFilteredRowsSelected(table.props);

  return (
    <div className="form-check" style={{ width: '1rem', margin: '0 auto' }}>
      <input
        className="form-check-input"
        type="checkbox"
        checked={areAllRowsSelected}
        onChange={(event) => {
          if (event.currentTarget.checked) {
            // Select all rows in the dataset
            table.selectAllRows();
          } else {
            table.deselectAllRows();
          }
        }}
      />
    </div>
  );
};

const GetComponents = (columnsList?: TableColumn[], allRows?: any): ChildComponents | undefined => {
  if (columnsList && columnsList.length > 0) {
    const componentList = columnsList.filter(
      (x) => x.component || x.valueConvert || x.hideHeaderFilter
    );
    if (componentList && componentList.length > 0) {
      return {
        headFilterButton: {
          content(props) {
            try {
              if ((props.column as TableColumn).hideHeaderFilter) {
                return <></>;
              }
            } catch {}
          },
        },
        cellText: {
          content: (props) => {
            if (props.column.key === "selection-cell") {
              return <SelectionCell {...props} />;
            }
            const columnFind = componentList.filter(
              (x) => x.key === props.column.key
            )[0];
            if (columnFind) {
              if (columnFind.component) {
                return <columnFind.component {...props} />;
              } else if (columnFind.valueConvert) {
                return columnFind.valueConvert(
                  props.rowData[props.column.key],
                  props.rowData
                );
              }
            }
          },
        },
        headCell: {
          content: (props: { column: TableColumn }) => {
            if (props.column.key === "selection-cell") {
              return <SelectionHeader allRows={allRows || []} />;
            }
            const destinationColumn = props.column.destinationColumn || "";
            const destinationLName = props.column.destinationLName || "";
            const title = props.column.title || "";

            return (
              <div>
                <span>{title}</span>
                {destinationColumn && (
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.8em",
                      color: "#888",
                      fontWeight: "600",
                    }}
                    title={`Mapped to: ${destinationColumn}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-link-45deg"
                      viewBox="0 0 16 16"
                    >
                      <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z" />
                      <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 0 0-4.243-4.243z" />
                    </svg>
                    {destinationLName ?? ""}
                  </span>
                )}
              </div>
            );
          },
        },
      };
    }
  }
  return undefined;
};

let tableComponents: ChildComponents = {
  table: {
    elementAttributes: () => ({
      className: "table table-striped table-hover",
    }),
  },
  noDataRow: {
    content: () => "No Data Found",
  },
  pagingIndex: {
    elementAttributes: ({ isActive }) => ({
      className: `page-item ${isActive ? "active" : ""}`,
    }),
    content: ({ text, isActive }) => (
      <a
        className={`page-link ${isActive ? "active" : ""}`}
        href="javascript:void(0)"
      >
        {text}
      </a>
    ),
  },
  pagingPages: {
    elementAttributes: () => ({
      className: "pagination",
    }),
    content(props) {
      return <PagingPages {...props} />;
    },
  },
  pagingSizes: {
    content: (props) => {
      return (
        <FormGroup>
          <Input
            type="select"
            value={props.pageSize}
            onChange={(e) => {
              props.dispatch(updatePageSize(Number(e.target.value)));
            }}
          >
            {props.pageSizes?.map((size) => (
              <option>{size}</option>
            ))}
          </Input>
        </FormGroup>
      );
    },
  },
};

const addTableChildComponents = (props: any): ChildComponents => {
  const { onRowDoubleClick, hideRowHeader } = props;
  let result = {};
  if (onRowDoubleClick) {
    result = {
      dataRow: {
        elementAttributes: () => ({
          onDoubleClick: (
            event: React.MouseEvent<HTMLElement, MouseEvent>,
            extendedEvent: AttributeTableData<IDataRowProps>
          ) => {
            onRowDoubleClick(event, extendedEvent);
          },
        }),
      },
    };
  } else {
    result = { dataRow: undefined };
  }
  result = {
    ...result,
    tableHead: {
      elementAttributes: () => ({
        className: "thead-dark",
      }),
      content: (propsContent: any) => (hideRowHeader ? <></> : undefined),
    },
  };
  return result;
};

const DataView = (props: any) => {
  const {
    html,
    columns,
    rows,
    allRows,
    onSelectionChange,
    rowCount,
    limit,
    striped = false,
    isLoading = false,
    hideRowHeader = false,
    keyId = "id",
    selectedRows,
    isSearchDisable,
    currentPage,
    setCurrentPage,
  } = props;
  const [searchText, setSearchText] = useState("");
  const components = GetComponents(columns, allRows);
  const paging = props.updatePagination
    ? {
        paging: {
          enabled: true,
          pageIndex: currentPage ? Number(currentPage) : 0,
          pagesCount: rowCount ? Math.ceil(rowCount / limit) : 1,
          pageSize: limit,
          pageSizes: [10, 20, 50, 100, 500, 800, 1000],
          position: PagingPosition.Bottom,
        },
      }
    : {
        paging: {
          enabled: true,
          pageIndex: 0,
          pageSize: 10,
          pageSizes: [10, 20, 50, 100, 500, 800, 1000],
          position: PagingPosition.Bottom,
        },
      };

  const table = useTable({
    onDispatch: async (action) => {
      if (
        props.onSelectionChange &&
        (action.type === ActionType.SelectRow ||
          action.type === ActionType.SelectAllRows ||
          action.type === ActionType.DeselectAllRows ||
          action.type === ActionType.DeselectRow)
      ) {
        if (action.type === ActionType.SelectRow || action.type === ActionType.DeselectRow) {
          const rowKeyValue = action.rowKeyValue;
          onSelectionChange(action.type, rowKeyValue);
        } else if (action.type === ActionType.SelectAllRows || action.type === ActionType.DeselectAllRows) {
          // Handle bulk selection actions for all rows
          if (action.type === ActionType.SelectAllRows) {
            const allRowKeys = (allRows || []).map((row: any, index: number) => index + 1); // Use 1-based indexing for tableId
            onSelectionChange(action.type, allRowKeys);
          } else if (action.type === ActionType.DeselectAllRows) {
            onSelectionChange(action.type, []);
          }
        }
      }
      if (props.updatePagination) {
        if (action.type === ActionType.UpdatePageIndex) {
          props.updatePagination().updateCurrentPageNo(action.pageIndex);
        } else if (action.type === ActionType.UpdatePageSize) {
          props.updatePagination().updatePageLimit(action.pageSize);
        }
      }
    },
  });

  useEffect(() => {
    if (isSearchDisable) {
      table.selectAllRows();
    }
  }, []);

  if (components) {
    tableComponents = { ...tableComponents, ...components };
  }
  tableComponents = { ...tableComponents, ...addTableChildComponents(props) };
  const tableText = useMemo(() => {
    return (
      <Table
        key={generateUniqueId()}
        table={table}
        {...paging}
        childComponents={tableComponents}
        columns={columns}
        data={rows}
        selectedRows={selectedRows}
        filteringMode={FilteringMode.HeaderFilter}
        rowKeyField={keyId}
        searchText={searchText}
        sortingMode={SortingMode.Single}
      />
    );
  }, [rows, columns, searchText, selectedRows]);

  return (
    <div className="data-table data-table-wrapper">
      <div className="d-flex justify-content-between mb-3">
        <div className=""></div>
        <div className="search_box">
          {!isSearchDisable && (
            <Button color="link" className="p-0">
              {searchIcon.icon}
            </Button>
          )}
          <input
            name="searchbox"
            placeholder="Search.."
            type="search"
            value={searchText}
            onChange={(event) => {
              setSearchText(event.currentTarget.value);
            }}
            className="top-element"
          />
        </div>
      </div>
      {tableText}
    </div>
  );
};

export { DataView };